import type {ColorModeWithAuto} from '../../assets/modules/github/color-modes'
import {controller} from '@github/catalyst'

// Matches simple integer or decimal values with units (e.g., 123px, 1.23em,
// 50%). Does not match values less than one. Does not match decimal values
// with more than nine decimal places.
//
// Changes to this pattern should also be reflected in the
// GitHub::Goomba::ImageStyleFilter class's similarly named constant.
const DIMENSION_PATTERN = /^\s*[1-9]\d*(?:\.\d{1,9})?(?:[a-z]+|%)$/i

@controller
class ThemedPictureElement extends HTMLElement {
  #mutationObserver!: MutationObserver

  connectedCallback() {
    // if the first child (the picture element) is already loaded we can apply theming now.
    // we also attach a MutationObserver to wait for the picture to load or any updates to the child nodes.
    // eslint-disable-next-line wc/no-child-traversal-in-connectedcallback
    if (this.firstElementChild !== null) {
      this.applyTheming()
    }

    this.#mutationObserver = new MutationObserver(() => {
      this.applyTheming()
    })

    this.#mutationObserver.observe(this, {childList: true, subtree: true, attributes: false})
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect()
  }

  applyTheming() {
    const mode = this.#getColorMode()
    const img = this.querySelector('img')
    const picture = this.firstElementChild as HTMLPictureElement

    // Handle auto mode early
    if (mode === 'auto') {
      this.#makeElementVisible(img)
      return
    }

    // Check if this picture has themed sources once
    const hasThemedSources = this.#hasThemedSources()

    if (hasThemedSources) {
      this.applyColorModeToPicture(picture, mode)
      this.#setupImageLoadHandler(img)
    } else {
      // Simple picture without themed sources - show immediately
      this.#makeElementVisible(img)
    }
  }

  #getColorMode(): ColorModeWithAuto {
    const mode = this.ownerDocument
      .querySelector('html[data-color-mode]')
      ?.getAttribute('data-color-mode') as ColorModeWithAuto

    // Default to light if no mode is set
    return mode ?? 'light'
  }

  #hasThemedSources(): boolean {
    const sources = this.querySelectorAll('source')
    return Array.from(sources).some(source => {
      const media = source.getAttribute('media') ?? ''
      return media.includes('prefers-color-scheme')
    })
  }

  #setupImageLoadHandler(img: HTMLImageElement | null): void {
    if (!img) return

    // Only add load handler if image hasn't loaded yet
    if (img.complete) {
      this.#makeElementVisible(img)
    } else {
      // Use once: true to automatically remove the listener
      img.addEventListener(
        'load',
        () => {
          this.#makeElementVisible(img)
        },
        {once: true},
      )
    }
  }

  #makeElementVisible(img: HTMLImageElement | null): void {
    // Make the themed-picture container visible
    this.style.visibility = 'visible'

    // Make the image visible if it exists
    if (img) {
      this.makeImageVisible(img)
    }
  }

  private applyColorModeToPicture(picture: HTMLPictureElement, mode: ColorModeWithAuto) {
    const sources = picture.querySelectorAll('source')
    const sourcesLight: HTMLSourceElement[] = []
    const sourcesDark: HTMLSourceElement[] = []

    this.saveSourceThemes(sources, sourcesLight, sourcesDark)
    this.updatePictureTheme(mode, sourcesLight, sourcesDark)
  }

  private saveSourceThemes(
    sources: NodeListOf<HTMLSourceElement>,
    sourcesLight: HTMLSourceElement[],
    sourcesDark: HTMLSourceElement[],
  ) {
    for (const source of sources ?? []) {
      const media = source.getAttribute('media') ?? ''
      const theme = this.getSourceTheme(media)

      // invalid media theme or 'all'
      if (theme === null) continue

      // store the original source theme for when we need to update theming without page load
      if (!(source.classList.contains('source-light') || source.classList.contains('source-dark'))) {
        source.classList.add(`source-${theme}`)
      }

      if (source.classList.contains('source-light')) {
        sourcesLight.push(source)
      } else if (source.classList.contains('source-dark')) {
        sourcesDark.push(source)
      }
    }
  }

  // Based on mode set media to '(prefers-color-scheme: light),(prefers-color-scheme: dark)'
  // for all sources with matching theme, otherwise set to 'not all'
  private updatePictureTheme(
    mode: ColorModeWithAuto,
    sourcesLight: HTMLSourceElement[],
    sourcesDark: HTMLSourceElement[],
  ) {
    if (mode === 'light') {
      this.toggleSources({off: sourcesDark, on: sourcesLight})
    } else if (mode === 'dark') {
      this.toggleSources({off: sourcesLight, on: sourcesDark})
    }
  }

  private toggleSources({off, on}: {off: HTMLSourceElement[]; on: HTMLSourceElement[]}) {
    for (const source of on) {
      source.setAttribute('media', '(prefers-color-scheme: light),(prefers-color-scheme: dark)')
    }

    for (const source of off) {
      source.setAttribute('media', 'not all')
    }
  }

  private getSourceTheme(mediaQuery: string | null) {
    if (mediaQuery?.match('(prefers-color-scheme: light)')) {
      return 'light'
    } else if (mediaQuery?.match('(prefers-color-scheme: dark)')) {
      return 'dark'
    }
    return null
  }

  // Make the given image visible by setting its visibility and max-width.
  // Any width and/or height styles will also be preserved if they match our
  // pattern describing acceptable image sizing dimensions.
  private makeImageVisible(img: HTMLImageElement) {
    const newStyle = img.ownerDocument.createElement('img').style

    const width = img.style.width
    if (width && DIMENSION_PATTERN.test(width)) {
      newStyle.width = width
    }

    const height = img.style.height
    if (height && DIMENSION_PATTERN.test(height)) {
      newStyle.height = height
    }

    newStyle.visibility = 'visible'
    newStyle.maxWidth = '100%'

    img.style.cssText = newStyle.cssText
  }
}
